import { Lock, Clock, Sparkles, BookOpen } from "lucide-react";
import { ADVICE, ACTIVITIES } from "@/data/parentingData";
import { useChildren } from "@/context/ChildrenContext";
import Mascot from "@/components/Mascot";

const bucketFor = (age) => {
  if (typeof age !== "number") return "3-5";
  if (age <= 2) return "0-2";
  if (age <= 5) return "3-5";
  if (age <= 9) return "6-9";
  if (age <= 12) return "10-12";
  return "13+";
};

const dayIndex = () => Math.floor(Date.now() / 86400000);

// Aperçu du jour pour les comptes verrouillés : on montre le début, on floute la suite.
export default function WelcomeTeaser() {
  const { activeChild } = useChildren();
  const bucket = bucketFor(activeChild?.age);
  const d = dayIndex();

  const tips = ADVICE.filter((a) => a.age === bucket);
  const tip = tips.length ? tips[d % tips.length] : ADVICE[0];
  const acts = ACTIVITIES.filter((a) => a.age === bucket);
  const act = acts.length ? acts[d % acts.length] : ACTIVITIES[0];

  const [firstSentence, ...rest] = tip.body.split(/(?<=\.)\s+/);

  return (
    <section className="space-y-4" data-testid="welcome-teaser">
      <div className="flex items-center gap-3">
        <Mascot pose="idea" size={78} anim="float" className="shrink-0 mascot-pop" />
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">
            Aperçu du jour
          </p>
          <h2 className="text-base md:text-lg font-semibold text-[#16233D]">
            Voilà ce que Kynoria te prépare pour{" "}
            {activeChild ? activeChild.name : "ton enfant"} aujourd'hui
          </h2>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 items-start">
        {/* Conseil du jour */}
        <article className="soft-card p-6 pastel-sage !bg-[#D2EDE1]" data-testid="teaser-advice">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#16233D]">
            <BookOpen className="h-4 w-4" /> Conseil du jour · {tip.category}
          </div>
          <h3 className="mt-2 text-xl font-semibold text-[#16233D]">{tip.title}</h3>
          <p className="mt-2 text-[#23375E] leading-relaxed">{firstSentence}</p>
          <div className="relative mt-1">
            <p className="text-[#23375E] leading-relaxed blur-[5px] select-none" aria-hidden="true">
              {rest.join(" ") || tip.body}
            </p>
            <div className="absolute inset-0 grid place-items-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-[#16233D]">
                <Lock className="h-3.5 w-3.5" /> Suite verrouillée
              </span>
            </div>
          </div>
          <p className="mt-3 text-xs text-[#23375E]">
            + {ADVICE.length - 1} autres conseils adaptés à l'âge exact de ton enfant
          </p>
        </article>

        {/* Activité du jour */}
        <article className="soft-card p-6 pastel-lavender !bg-[#E3EAF7]" data-testid="teaser-activity">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#16233D]">
            <Sparkles className="h-4 w-4" /> Activité à faire · {act.type}
          </div>
          <h3 className="mt-2 text-xl font-semibold text-[#16233D]">{act.title}</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="age-badge bg-white/70 inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> {act.duration}
            </span>
            <span className="age-badge bg-white/70">{act.ages}</span>
          </div>
          <p className="mt-3 text-sm font-semibold text-[#16233D]">Matériel</p>
          <p className="text-sm text-[#23375E]">{act.materials.join(" · ")}</p>
          <p className="mt-3 text-sm font-semibold text-[#16233D]">Les étapes</p>
          <div className="relative mt-1">
            <ol className="space-y-1 blur-[5px] select-none" aria-hidden="true">
              {act.steps.map((s, i) => (
                <li key={i} className="text-sm text-[#23375E]">
                  {i + 1}. {s}
                </li>
              ))}
            </ol>
            <div className="absolute inset-0 grid place-items-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-[#16233D]">
                <Lock className="h-3.5 w-3.5" /> {act.steps.length} étapes verrouillées
              </span>
            </div>
          </div>
          <p className="mt-3 text-xs text-[#23375E]">
            + {ACTIVITIES.length - 1} autres activités, une nouvelle chaque jour
          </p>
        </article>
      </div>
    </section>
  );
}
